import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import './with-overflow-borders.scss';

const WithOverflowBorders = ({ children, className, hasFooterElements }) => {
  const wrapper = useRef(null);

  const overflowChecker = () => {
    // content refers to the actual content that is longer than the container
    // container refers to the element that contains the content and has a
    // fixed height and will have the overflow borders.
    // if content and container have the same height then the content is not scrollable
    // hence isScrollable will be false and the overflow borders will not be added
    const content = wrapper?.current?.children[0];
    const contentHeight = content?.scrollHeight;
    const containerHeight = content?.clientHeight;
    const isScrollable = contentHeight > containerHeight;

    if (isScrollable) {
      if (hasFooterElements) {
        content?.classList.add('overflow-b-bottom');
      }
      const { scrollTop } = content;
      // ^ scrollTop is the distance from the top of the content to the top of the container
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + containerHeight >= contentHeight;
      // >= is used instead of === in the above line because of an odd bug where
      // the scrollTop value is 0.5px more than the actual top offset of the content,
      // which causes the isAtBottom to be false even when the content is scrolled to the bottom
      if (isAtTop) {
        content?.classList.remove('overflow-b-top');
      } else {
        content?.classList.add('overflow-b-top');
      }

      if (!isAtBottom && hasFooterElements) {
        content?.classList.add('overflow-b-bottom');
      } else {
        content?.classList.remove('overflow-b-bottom');
      }
    }
  };
  useEffect(() => {
    const content = wrapper?.current?.children[0];
    const observer = new ResizeObserver(overflowChecker);
    content.addEventListener('scroll', overflowChecker);
    observer.observe(content);

    // cleanup
    return () => {
      observer.disconnect();
      content?.removeEventListener('scroll', overflowChecker);
    };
  }, []);

  return (
    <div ref={wrapper} className={clsx('overflow-border-cont', className)}>
      {children}
    </div>
  );
};

WithOverflowBorders.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  hasFooterElements: PropTypes.bool,
};

WithOverflowBorders.defaultProps = {
  className: '',
  hasFooterElements: true,
};

export default WithOverflowBorders;
